/* =========================================================================
   ClueVine — Profile context (account-lite)
   A purely local identity: a generated id, a chosen name + botanical avatar,
   and a created-at stamp. No server, no auth. `null` until onboarding finishes,
   which is what gates the first-run flow.
   ========================================================================= */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { uuid } from '../utils/id';
import { loadSlice, saveSlice, type SliceConfig } from './persistence';

/** Avatar ids — rendered as botanical glyphs by <Avatar />. */
export const AVATARS = ['sprout', 'leaf', 'fern', 'bud', 'clover', 'flower', 'seed', 'palm'] as const;
export type AvatarId = (typeof AVATARS)[number];

export interface Profile {
  id: string;
  name: string;
  avatar: AvatarId;
  createdAt: number;
}

const MAX_NAME = 24;

/** Validate a stored or imported profile; null if it isn't one. */
export function normalizeProfile(raw: unknown): Profile | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== 'string' || !r.id || typeof r.name !== 'string') return null;
  const name = r.name.trim().slice(0, MAX_NAME) || 'Player';
  const avatar = (AVATARS as readonly string[]).includes(r.avatar as string) ? (r.avatar as AvatarId) : 'sprout';
  const createdAt = typeof r.createdAt === 'number' && Number.isFinite(r.createdAt) ? r.createdAt : Date.now();
  return { id: r.id, name, avatar, createdAt };
}

const slice: SliceConfig<Profile | null> = {
  name: 'profile',
  version: 1,
  defaults: () => null,
};

interface ProfileContextValue {
  profile: Profile | null;
  create: (name: string, avatar: AvatarId) => Profile;
  update: (patch: Partial<Pick<Profile, 'name' | 'avatar'>>) => void;
  replaceAll: (next: unknown) => void;
  reset: () => void;
}

const ProfileContext = createContext<ProfileContextValue | null>(null);

export function ProfileProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<Profile | null>(() => normalizeProfile(loadSlice(slice)));

  useEffect(() => {
    saveSlice(slice, profile);
  }, [profile]);

  const create = useCallback((name: string, avatar: AvatarId) => {
    const next: Profile = {
      id: uuid(),
      name: name.trim().slice(0, MAX_NAME) || 'Player',
      avatar,
      createdAt: Date.now(),
    };
    setProfile(next);
    return next;
  }, []);

  const update = useCallback((patch: Partial<Pick<Profile, 'name' | 'avatar'>>) => {
    setProfile((p) =>
      p ? { ...p, ...patch, name: (patch.name ?? p.name).trim().slice(0, MAX_NAME) || p.name } : p,
    );
  }, []);

  const replaceAll = useCallback((next: unknown) => setProfile(normalizeProfile(next)), []);
  const reset = useCallback(() => setProfile(null), []);

  const value = useMemo<ProfileContextValue>(
    () => ({ profile, create, update, replaceAll, reset }),
    [profile, create, update, replaceAll, reset],
  );

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
}

export function useProfile(): ProfileContextValue {
  const ctx = useContext(ProfileContext);
  if (!ctx) throw new Error('useProfile must be used within <ProfileProvider>');
  return ctx;
}
